import React from 'react';
import PropTypes from 'prop-types';
import { Translate, withLocalize } from 'react-localize-redux';
import classNames from 'classnames';
import Icon, { IconSizes, IconVariants } from '../../components/foundations/Icon';
import QuestionMark from '../../components/widgets/QuestionMark';
import css from './DataConnectorTimezone.css';
import { TIMEZONE_LEVELS, TIMEZONE_TYPES, formatTimezoneOffset } from '../utils';

const TRANSLATION_PREFIX = 'STATIC.PAGES.DATA_SOURCES.SHELF.TIMEZONE';

export const timezoneInfoShape = PropTypes.shape({
    timezone: PropTypes.string,
    utc_offset_hours: PropTypes.number,
    utc_offset_string: PropTypes.string,
    timezone_type: PropTypes.string,
    level: PropTypes.string,
});

// Each case in the connector timezone design pairs the source note on the right of the bar with its own
// tooltip, so both are resolved together from the timezone type and the level the value resolved at.
const STATIC_SOURCE = {
    source: `${TRANSLATION_PREFIX}.SOURCE_STATIC`,
    tooltip: `${TRANSLATION_PREFIX}.TOOLTIP_STATIC`,
};

// dynamic_platform_side: the advertiser sets it on the network, Singular can only read it back
const REPORTED_SOURCE = {
    source: `${TRANSLATION_PREFIX}.SOURCE_REPORTED`,
    tooltip: `${TRANSLATION_PREFIX}.TOOLTIP_REPORTED`,
};

// dynamic_query: Singular sends the timezone on the request, so the wording says where Singular took it from
const CONNECTOR_SOURCE = {
    source: `${TRANSLATION_PREFIX}.SOURCE_CONNECTOR`,
    tooltip: `${TRANSLATION_PREFIX}.TOOLTIP_CONNECTOR`,
};

const ORGANIZATION_SOURCE = {
    source: `${TRANSLATION_PREFIX}.SOURCE_CONNECTOR`,
    tooltip: `${TRANSLATION_PREFIX}.TOOLTIP_ORGANIZATION`,
};

const SOURCE_BY_LEVEL = {
    [TIMEZONE_LEVELS.ACCOUNT]: REPORTED_SOURCE,
    [TIMEZONE_LEVELS.UAN]: REPORTED_SOURCE,
    [TIMEZONE_LEVELS.ORGANIZATION]: ORGANIZATION_SOURCE,
    [TIMEZONE_LEVELS.ADNETWORK]: STATIC_SOURCE,
    [TIMEZONE_LEVELS.DEFAULT]: ORGANIZATION_SOURCE,
};

const DYNAMIC_QUERY_SOURCE_BY_LEVEL = {
    ...SOURCE_BY_LEVEL,
    [TIMEZONE_LEVELS.UAN]: CONNECTOR_SOURCE,
    [TIMEZONE_LEVELS.ORGANIZATION]: ORGANIZATION_SOURCE,
    [TIMEZONE_LEVELS.ADNETWORK]: CONNECTOR_SOURCE,
};

const getSource = timezone => {
    if (timezone.timezone_type === TIMEZONE_TYPES.STATIC) {
        return STATIC_SOURCE;
    }
    return (timezone.timezone_type === TIMEZONE_TYPES.DYNAMIC_QUERY ? DYNAMIC_QUERY_SOURCE_BY_LEVEL : SOURCE_BY_LEVEL)[
        timezone.level
    ];
};

const getTooltipId = (timezone, multiple) => {
    if (multiple) {
        return `${TRANSLATION_PREFIX}.TOOLTIP_MULTIPLE`;
    }
    const source = getSource(timezone);
    return source ? source.tooltip : `${TRANSLATION_PREFIX}.TOOLTIP`;
};

function DataConnectorTimezone({
    timezone = null,
    multiple = false,
    adnetworkName = '',
    className = '',
    translate = key => key,
}) {
    if (!multiple && (!timezone || !timezone.timezone)) {
        return null;
    }

    return (
        <div className={classNames(css.container, className)}>
            <Icon name="world" size={IconSizes.SMALL} variant={IconVariants.LIGHT} className={css.icon} />
            <span className={css.label}>
                <Translate id={multiple ? `${TRANSLATION_PREFIX}.MULTIPLE_LABEL` : `${TRANSLATION_PREFIX}.LABEL`} />
            </span>
            {multiple ? (
                <span className={css.value}>
                    <Translate id={`${TRANSLATION_PREFIX}.ACCOUNT_LEVEL`} />
                </span>
            ) : (
                <>
                    <span className={css.value}>{timezone.timezone}</span>
                    <span className={css.offset}>{formatTimezoneOffset(timezone)}</span>
                    {getSource(timezone) && (
                        <span className={css.source}>
                            <Translate id={getSource(timezone).source} data={{ adnetwork: adnetworkName }} />
                        </span>
                    )}
                </>
            )}
            <QuestionMark
                containerClassName={css.questionMark}
                message={translate(getTooltipId(timezone, multiple), { adnetwork: adnetworkName })}
            />
        </div>
    );
}

DataConnectorTimezone.propTypes = {
    timezone: timezoneInfoShape,
    // Account based connectors show "Multiple" instead of a single value, with the values in the accounts table
    multiple: PropTypes.bool,
    adnetworkName: PropTypes.string,
    className: PropTypes.string,
    translate: PropTypes.func,
};

export default withLocalize(DataConnectorTimezone);
